import * as AccordionPrimitive from '@radix-ui/react-accordion';
import React from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '@/auth/UserContext';
import { DetailLayout } from '@/components/ProfileMenu/DetailLayout';
import { EmailSection } from '@/components/ProfileMenu/NotificationsView/EmailSection';
import { NotificationCard } from '@/components/ProfileMenu/NotificationsView/NotificationCard';
import { PushSection } from '@/components/ProfileMenu/NotificationsView/PushSection';
import { SmsSection } from '@/components/ProfileMenu/NotificationsView/SmsSection';
import { useNotificationSettings } from '@/components/ProfileMenu/NotificationsView/hooks/useNotificationSettings';
import { useUpdateNotificationSettings } from '@/components/ProfileMenu/NotificationsView/hooks/useUpdateNotificationSettings';
import { BellCircle } from '@/components/Icons/BellCircle';
import { MailCircle } from '@/components/Icons/MailCircle';
import { SmsCircle } from '@/components/Icons/SmsCircle';
import { User } from '@/components/Icons/User';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { ROUTE_SUBSCRIPTION_PAYMENT_OPTIONS } from '@/router/routes';
import { NotificationChannel } from '@/types/notifications';
import type { FamilyMember } from '@/types/user';
import { getNameWithRole } from '@/utils/person';

interface Props {
  member?: FamilyMember;
  onBack?: () => void;
  onClose: () => void;
  onError: () => void;
  onChangePhone: () => void;
  onChangeEmail: () => void;
}

export const NotificationsView: React.FC<Props> = ({
  member,
  onBack,
  onClose,
  onError,
  onChangePhone,
  onChangeEmail,
}) => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const navigate = useNavigate();

  const isViewOnly = !!member;
  const userPublicId = member?.userPublicId ?? user?.userPublicId;
  const phoneNumber = member ? member.phoneNumber : user?.phoneNumber;
  const emailAddress = member ? member.emailAddress : user?.emailAddress;
  const isSubscribed = user?.premiumSubscription?.isActive ?? false;
  const headerName = getNameWithRole({
    person: user,
    personType: member?.personType,
    lowercaseRole: true,
  });

  const { data, isLoading } = useNotificationSettings(userPublicId);
  const { updateSettings } = useUpdateNotificationSettings(userPublicId, {
    onError,
  });

  const handlePurchase = () => {
    navigate(ROUTE_SUBSCRIPTION_PAYMENT_OPTIONS);
    onClose();
  };

  return (
    <DetailLayout
      title={t('notificationSettings.title')}
      onBack={onBack}
      onClose={onClose}
    >
      {(isLoading || !data) && (
        <div className="flex flex-1 items-center justify-center">
          <PageSpinner className="size-[20px] text-subtle-foreground" />
        </div>
      )}

      {!isLoading && data && (
        <>
          {isViewOnly && (
            <div className="px-1">
              <div className="flex items-start gap-3 pt-2">
                <User className="size-6 shrink-0 text-foreground" />
                <div className="flex min-w-0 flex-col gap-0.5 md:max-w-[260px]">
                  <p className="truncate text-body-16 text-foreground">
                    {headerName}
                  </p>
                  <p className="text-caption-12 text-subtle-foreground">
                    {t('notificationSettings.viewOnlyHeaderDescription')}
                  </p>
                </div>
              </div>
              <div className="mt-4 h-px bg-divider" />
            </div>
          )}

          <AccordionPrimitive.Root
            type="multiple"
            className={`flex flex-col gap-2 ${isViewOnly ? 'pt-5' : 'pt-2'}`}
          >
            <NotificationCard
              channel={NotificationChannel.Sms}
              icon={<SmsCircle className="size-8 shrink-0" />}
              title={t('notificationSettings.smsTitle')}
              subtitle={phoneNumber ?? t('notificationSettings.phoneNotAdded')}
              isSubtitleAttention={!phoneNumber}
            >
              <SmsSection
                settings={data.sms}
                isSubscribed={isSubscribed}
                hasPhone={!!phoneNumber}
                viewOnly={isViewOnly}
                onChange={(sms) => updateSettings({ ...data, sms })}
                onChangePhone={onChangePhone}
                onPurchase={handlePurchase}
              />
            </NotificationCard>

            <NotificationCard
              channel={NotificationChannel.Email}
              icon={<MailCircle className="size-8 shrink-0" />}
              title={t('notificationSettings.emailTitle')}
              subtitle={emailAddress ?? t('notificationSettings.emailNotAdded')}
              isSubtitleAttention={!emailAddress}
            >
              <EmailSection
                settings={data.email}
                isSubscribed={isSubscribed}
                hasEmail={!!emailAddress}
                viewOnly={isViewOnly}
                onChange={(email) => updateSettings({ ...data, email })}
                onChangeEmail={onChangeEmail}
                onPurchase={handlePurchase}
              />
            </NotificationCard>

            <NotificationCard
              channel={NotificationChannel.Push}
              icon={<BellCircle className="size-8 shrink-0" />}
              title={t('notificationSettings.pushTitle')}
              subtitle={t('notificationSettings.pushSubtitle')}
            >
              <PushSection
                isSubscribed={isSubscribed}
                viewOnly={isViewOnly}
                onPurchase={handlePurchase}
              />
            </NotificationCard>
          </AccordionPrimitive.Root>
        </>
      )}
    </DetailLayout>
  );
};
